class YtteronHomepage {
  constructor(root = document) {
    this.root = root;
    this.cleanups = [];
    this.init();
  }

  init() {
    this.initAnnouncement();
    this.initHeader();
    this.initMegaMenus();
    this.initLocalization();
    this.initMobileMenu();
    this.initTabs();
    this.initFinder();
    this.initFooterDetails();
  }

  addCleanup(fn) {
    this.cleanups.push(fn);
  }

  destroy() {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
  }

  initAnnouncement() {
    this.root.querySelectorAll('[data-yt-announcement]').forEach((bar) => {
      const messages = Array.from(bar.querySelectorAll('[data-yt-announcement-message]'));
      const close = bar.querySelector('[data-yt-announcement-close]');
      const rotate = bar.dataset.rotate === 'true';
      const interval = Number(bar.dataset.interval || 5000);
      let index = 0;
      let timer = null;

      const show = (next) => {
        messages.forEach((message, messageIndex) => {
          message.classList.toggle('is-active', messageIndex === next);
        });
      };

      show(0);

      if (rotate && messages.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        timer = window.setInterval(() => {
          index = (index + 1) % messages.length;
          show(index);
        }, interval);
        this.addCleanup(() => window.clearInterval(timer));
      }

      if (close) {
        const onClose = () => {
          bar.dataset.hidden = 'true';
        };
        close.addEventListener('click', onClose);
        this.addCleanup(() => close.removeEventListener('click', onClose));
      }
    });
  }

  initHeader() {
    this.root.querySelectorAll('[data-yt-header]').forEach((header) => {
      const pageWrapper = document.querySelector('.page-wrapper');
      const desktopQuery = window.matchMedia('(min-width: 990px)');
      const headerFrame = header.closest('.shopify-section') || header;
      const existingSpacer = header.nextElementSibling?.classList?.contains('yt-header-spacer') ? header.nextElementSibling : null;
      const spacer = existingSpacer || document.createElement('div');
      if (!existingSpacer) {
        spacer.className = 'yt-header-spacer';
        header.after(spacer);
      }
      spacer.setAttribute('aria-hidden', 'true');
      let ticking = false;
      let headerHeight = 0;
      let stickyStart = 0;

      const getScrollTop = () => {
        if (desktopQuery.matches && pageWrapper) return pageWrapper.scrollTop;
        return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      };

      const getStickyStart = () => {
        const y = getScrollTop();
        if (desktopQuery.matches && pageWrapper) {
          const containerRect = pageWrapper.getBoundingClientRect();
          const frameRect = headerFrame.getBoundingClientRect();
          return frameRect.top - containerRect.top + y;
        }
        return headerFrame.getBoundingClientRect().top + y;
      };

      const syncHeaderOffset = () => {
        headerHeight = Math.round(header.getBoundingClientRect().height);
        if (!headerHeight) return;
        stickyStart = getStickyStart();
        spacer.style.height = header.classList.contains('is-smart-sticky') ? `${headerHeight}px` : '0px';
        document.documentElement.style.setProperty('--yt-sticky-header-height', `${headerHeight}px`);
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      };

      const update = () => {
        const y = getScrollTop();
        const shouldStick = y >= Math.max(1, stickyStart);
        header.classList.toggle('is-smart-sticky', shouldStick);
        header.classList.toggle('is-scrolled', shouldStick || y > 8);
        spacer.style.height = shouldStick ? `${headerHeight}px` : '0px';
        ticking = false;
      };

      const requestUpdate = () => {
        syncHeaderOffset();
        onScroll();
      };

      const onScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      };

      const scrollTargets = new Set([window, document]);
      if (pageWrapper) scrollTargets.add(pageWrapper);
      const addMediaListener = () => {
        if (desktopQuery.addEventListener) {
          desktopQuery.addEventListener('change', requestUpdate);
        } else {
          desktopQuery.addListener(requestUpdate);
        }
      };
      const removeMediaListener = () => {
        if (desktopQuery.removeEventListener) {
          desktopQuery.removeEventListener('change', requestUpdate);
        } else {
          desktopQuery.removeListener(requestUpdate);
        }
      };

      syncHeaderOffset();
      update();
      scrollTargets.forEach((target) => target.addEventListener('scroll', onScroll, { passive: true }));
      window.addEventListener('resize', requestUpdate, { passive: true });
      addMediaListener();

      let resizeObserver = null;
      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(requestUpdate);
        resizeObserver.observe(header);
      }

      this.addCleanup(() => {
        scrollTargets.forEach((target) => target.removeEventListener('scroll', onScroll));
        window.removeEventListener('resize', requestUpdate);
        removeMediaListener();
        resizeObserver?.disconnect();
        header.classList.remove('is-smart-sticky');
        if (!existingSpacer) spacer.remove();
      });
    });
  }

  initMegaMenus() {
    const megaItems = Array.from(this.root.querySelectorAll('[data-yt-mega-item]'));
    if (!megaItems.length) return;

    const closeItem = (item) => {
      const trigger = item.querySelector('[data-yt-mega-trigger]');
      item.classList.remove('is-open');
      trigger?.setAttribute('aria-expanded', 'false');
    };

    const onDocumentClick = (event) => {
      megaItems.forEach((item) => {
        if (!item.contains(event.target)) closeItem(item);
      });
    };

    document.addEventListener('click', onDocumentClick);
    this.addCleanup(() => document.removeEventListener('click', onDocumentClick));

    megaItems.forEach((item) => {
      const button = item.querySelector('[data-yt-mega-trigger]');
      const panel = item.querySelector('[data-yt-mega-panel]');
      let closeTimer = null;
      if (!button || !panel) return;

      const open = () => {
        window.clearTimeout(closeTimer);
        item.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        closeItem(item);
      };
      const delayedClose = () => {
        closeTimer = window.setTimeout(close, 120);
      };
      const onKeydown = (event) => {
        if (event.key === 'Escape') {
          close();
          button.focus();
        }
      };
      const onFocusOut = (event) => {
        if (!item.contains(event.relatedTarget)) close();
      };

      item.addEventListener('pointerenter', open);
      item.addEventListener('pointerleave', delayedClose);
      button.addEventListener('click', (event) => {
        if (button.tagName !== 'A') event.preventDefault();
        item.classList.contains('is-open') ? close() : open();
      });
      item.addEventListener('keydown', onKeydown);
      item.addEventListener('focusout', onFocusOut);

      this.addCleanup(() => {
        item.removeEventListener('pointerenter', open);
        item.removeEventListener('pointerleave', delayedClose);
        item.removeEventListener('keydown', onKeydown);
        item.removeEventListener('focusout', onFocusOut);
        window.clearTimeout(closeTimer);
      });
    });
  }

  initLocalization() {
    const disclosures = Array.from(this.root.querySelectorAll('[data-yt-localization]'));
    if (!disclosures.length) return;

    const closeOthers = (active) => {
      disclosures.forEach((details) => {
        if (details !== active) details.removeAttribute('open');
      });
    };

    const onDocumentClick = (event) => {
      disclosures.forEach((details) => {
        if (!details.contains(event.target)) details.removeAttribute('open');
      });
    };

    document.addEventListener('click', onDocumentClick);
    this.addCleanup(() => document.removeEventListener('click', onDocumentClick));

    disclosures.forEach((details) => {
      const summary = details.querySelector('summary');
      const options = Array.from(details.querySelectorAll('[role="option"]'));

      const onToggle = () => {
        if (details.open) closeOthers(details);
      };

      const onKeydown = (event) => {
        if (event.key === 'Escape') {
          details.removeAttribute('open');
          summary?.focus();
          return;
        }

        if (!details.open || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;

        event.preventDefault();
        const current = options.indexOf(document.activeElement);
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        const fallbackIndex = event.key === 'ArrowDown' ? 0 : options.length - 1;
        const nextIndex = current >= 0 ? (current + direction + options.length) % options.length : fallbackIndex;
        options[nextIndex]?.focus();
      };

      details.addEventListener('toggle', onToggle);
      details.addEventListener('keydown', onKeydown);

      this.addCleanup(() => {
        details.removeEventListener('toggle', onToggle);
        details.removeEventListener('keydown', onKeydown);
      });
    });
  }

  initMobileMenu() {
    this.root.querySelectorAll('[data-yt-mobile-menu]').forEach((drawer) => {
      const id = drawer.id;
      const openers = document.querySelectorAll(`[data-yt-mobile-open="${id}"]`);
      const closers = drawer.querySelectorAll('[data-yt-mobile-close]');
      const focusableSelector = 'a[href], button:not([disabled]), input, select, textarea, summary, [tabindex]:not([tabindex="-1"])';
      let previousFocus = null;

      const focusFirst = () => {
        const first = drawer.querySelector(focusableSelector);
        if (first) first.focus();
      };
      const open = () => {
        previousFocus = document.activeElement;
        drawer.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
        focusFirst();
      };
      const close = () => {
        drawer.setAttribute('aria-hidden', 'true');
        document.documentElement.style.overflow = '';
        if (previousFocus) previousFocus.focus();
      };
      const onKeydown = (event) => {
        if (event.key === 'Escape') close();
        if (event.key !== 'Tab' || drawer.getAttribute('aria-hidden') === 'true') return;
        const focusables = Array.from(drawer.querySelectorAll(focusableSelector));
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };

      openers.forEach((opener) => opener.addEventListener('click', open));
      closers.forEach((closer) => closer.addEventListener('click', close));
      drawer.addEventListener('keydown', onKeydown);

      this.addCleanup(() => {
        openers.forEach((opener) => opener.removeEventListener('click', open));
        closers.forEach((closer) => closer.removeEventListener('click', close));
        drawer.removeEventListener('keydown', onKeydown);
      });
    });
  }

  initTabs() {
    this.root.querySelectorAll('[data-yt-tabs]').forEach((tabs) => {
      const buttons = Array.from(tabs.querySelectorAll('[role="tab"]'));
      const panels = Array.from(tabs.querySelectorAll('[role="tabpanel"]'));
      const activate = (button) => {
        buttons.forEach((tab) => {
          tab.setAttribute('aria-selected', String(tab === button));
          tab.tabIndex = tab === button ? 0 : -1;
        });
        panels.forEach((panel) => panel.classList.toggle('is-active', panel.id === button.getAttribute('aria-controls')));
      };
      const onClick = (event) => activate(event.currentTarget);
      const onKeydown = (event) => {
        const current = buttons.indexOf(event.currentTarget);
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next = event.key === 'ArrowRight' ? (current + 1) % buttons.length : (current - 1 + buttons.length) % buttons.length;
        buttons[next].focus();
        activate(buttons[next]);
      };
      buttons.forEach((button, index) => {
        button.addEventListener('click', onClick);
        button.addEventListener('keydown', onKeydown);
        button.tabIndex = index === 0 ? 0 : -1;
      });
      this.addCleanup(() => {
        buttons.forEach((button) => {
          button.removeEventListener('click', onClick);
          button.removeEventListener('keydown', onKeydown);
        });
      });
    });
  }

  initFinder() {
    this.root.querySelectorAll('[data-yt-finder]').forEach((form) => {
      const onSubmit = (event) => {
        event.preventDefault();
        const data = new FormData(form);
        const base = form.dataset.destination || '/search';
        const url = new URL(base, window.location.origin);
        const terms = [];
        data.forEach((value, key) => {
          if (!value) return;
          url.searchParams.set(`filter.${key}`, value);
          terms.push(value);
        });
        if (url.pathname.includes('/search')) {
          url.searchParams.set('q', terms.join(' '));
          url.searchParams.set('type', 'product');
          url.searchParams.set('options[prefix]', 'last');
        }
        window.location.href = url.toString();
      };
      form.addEventListener('submit', onSubmit);
      this.addCleanup(() => form.removeEventListener('submit', onSubmit));
    });
  }

  initFooterDetails() {
    const media = window.matchMedia('(min-width: 990px)');
    const details = Array.from(this.root.querySelectorAll('[data-yt-footer-details]'));
    const sync = () => {
      details.forEach((detail) => {
        if (media.matches) detail.setAttribute('open', '');
      });
    };
    sync();
    media.addEventListener('change', sync);
    this.addCleanup(() => media.removeEventListener('change', sync));
  }
}

let ytteronHomepage = null;
const bootYtteron = (root = document) => {
  if (root === document && ytteronHomepage) ytteronHomepage.destroy();
  const instance = new YtteronHomepage(root);
  if (root === document) ytteronHomepage = instance;
};

bootYtteron();

document.addEventListener('shopify:section:load', (event) => {
  bootYtteron(event.target);
});
